import React from 'react';
import {
  Disclaimer,
  FEATURE_FLAGS,
  Footer,
  useGetAppInitDetails,
  useMobileAppDownloadLinks,
  EnhancedMembersDisclaimer,
  useMemberBrandingInfo,
} from '@highmark-web/highmark-common';
import { Image } from '@leagueplatform/genesis-commons';
import { useFooterLinks } from 'hooks/use-footer-links';
import { APP_STORE_PROD_URL, PLAY_STORE_PROD_URL } from 'common/constants';
import { SkeletonBox } from '@leagueplatform/web-common-components';
import { useGetAppFeatureFlag } from 'hooks/use-get-app-feature-flag.hook';
import { SOCIAL_MEDIA_ICONS } from './iel-footer.config';

export const IelFooter = React.memo(() => {
  const { footerLinks } = useFooterLinks();
  const { mobileAppDownloadLinks } = useMobileAppDownloadLinks(
    APP_STORE_PROD_URL,
    PLAY_STORE_PROD_URL,
  );
  const { isLoading, appMessaging } = useGetAppInitDetails();
  const { brandingInfo, shouldUseEnhancedMemberBranding } =
    useMemberBrandingInfo();

  const { value } = useGetAppFeatureFlag(FEATURE_FLAGS.CMS_CODE);

  if (isLoading)
    return (
      <SkeletonBox
        borderRadius="none"
        height="200px"
        backgroundColor="surface.background.secondary"
      />
    );

  // Append cms code to disclaimer if set in Feature flag
  const formattedFooterDisclaimerText = `${
    appMessaging?.disclaimer
  }\n${value.asString()}`;

  const brandingLogoAlt = (() => {
    if (shouldUseEnhancedMemberBranding) {
      if (brandingInfo?.brandingName) {
        return `${brandingInfo?.brandingName} Logo`;
      }
      return 'Provider Logo';
    }
    return 'MyHighmark Branding';
  })();

  // For enhanced members the logo and disclaimer are expected to be different.
  const branding = (
    <Image
      src={brandingInfo?.brandingLogo}
      alt={brandingLogoAlt}
      tabIndex={0}
      marginBottom="twoAndHalf"
      maxWidth={shouldUseEnhancedMemberBranding ? '170px' : undefined}
      width={shouldUseEnhancedMemberBranding ? 'unset' : '170px'}
    />
  );

  const disclaimer = shouldUseEnhancedMemberBranding ? (
    <EnhancedMembersDisclaimer />
  ) : (
    <Disclaimer hasLegalModal disclaimer={formattedFooterDisclaimerText} />
  );

  return (
    <Footer
      branding={branding}
      disclaimer={disclaimer}
      footerAppDownloadAssets={
        shouldUseEnhancedMemberBranding ? undefined : mobileAppDownloadLinks
      }
      footerIcons={
        shouldUseEnhancedMemberBranding ? undefined : SOCIAL_MEDIA_ICONS
      }
      footerLinks={shouldUseEnhancedMemberBranding ? undefined : footerLinks}
    />
  );
});
